/**
 * useSensorData Hook — Real-time sensor data from Heimdall
 *
 * Fetches latest readings, history, and device status for a given device.
 * Subscribes to Supabase Realtime for live updates.
 *
 * Supports multi-metric history via optional historyMetrics parameter.
 * Supports custom historySince timestamp to scope history window.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '@/supabase-client'

const HISTORY_HOURS = 24
const PAGE_SIZE = 1000

/**
 * Fetch all rows from a Supabase query, paginating through PAGE_SIZE chunks.
 * Returns the full result array.
 */
async function fetchAllPages(buildQuery) {
  const allData = []
  let page = 0
  while (true) {
    const from = page * PAGE_SIZE
    const to = from + PAGE_SIZE - 1
    const { data, error } = await buildQuery().range(from, to)
    if (error) throw error
    allData.push(...(data || []))
    if (!data || data.length < PAGE_SIZE) break
    page++
  }
  return allData
}

export function useSensorData(deviceType, deviceId, { historyMetrics = ['temperature'], historySince } = {}) {
  const [latest, setLatest] = useState({})       // { metric: { value, recorded_at } }
  const [historyMap, setHistoryMap] = useState({}) // { metric: [{ recorded_at, value }] }
  const [status, setStatus] = useState({})        // { status_key: { value, updated_at } }
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastUpdate, setLastUpdate] = useState(null)
  const mountedRef = useRef(true)
  const fetchVersionRef = useRef(0)
  const historyMetricsRef = useRef(historyMetrics)
  historyMetricsRef.current = historyMetrics

  // Stable ref for historySince to avoid unnecessary refetches
  const historySinceMs = historySince?.getTime() ?? null

  const fetchLatest = useCallback(async () => {
    const since = new Date(Date.now() - 60 * 60 * 1000).toISOString()
    const { data: readings, error: readError } = await supabase
      .from('sensor_readings')
      .select('metric, value, recorded_at')
      .eq('device_type', deviceType)
      .eq('device_id', deviceId)
      .gte('recorded_at', since)
      .order('recorded_at', { ascending: false })
      .limit(50)

    if (readError) throw readError

    const latestMap = {}
    for (const r of readings || []) {
      if (!latestMap[r.metric]) {
        latestMap[r.metric] = { value: r.value, recorded_at: r.recorded_at }
      }
    }
    return latestMap
  }, [deviceType, deviceId])

  const fetchHistory = useCallback(async () => {
    const since = historySinceMs
      ? new Date(historySinceMs).toISOString()
      : new Date(Date.now() - HISTORY_HOURS * 60 * 60 * 1000).toISOString()

    const results = await Promise.all(
      historyMetricsRef.current.map(async (metric) => {
        const data = await fetchAllPages(() =>
          supabase
            .from('sensor_readings')
            .select('recorded_at, value')
            .eq('device_type', deviceType)
            .eq('device_id', deviceId)
            .eq('metric', metric)
            .gte('recorded_at', since)
            .order('recorded_at', { ascending: true })
        )
        return { metric, data }
      })
    )

    const map = {}
    for (const { metric, data } of results) {
      map[metric] = data
    }
    return map
  }, [deviceType, deviceId, historySinceMs])

  const fetchStatus = useCallback(async () => {
    const { data, error: statusError } = await supabase
      .from('device_status')
      .select('status_key, value, updated_at')
      .eq('device_type', deviceType)
      .eq('device_id', deviceId)

    if (statusError) throw statusError

    const statusMap = {}
    for (const s of data || []) {
      statusMap[s.status_key] = { value: s.value, updated_at: s.updated_at }
    }
    return statusMap
  }, [deviceType, deviceId])

  const fetchAll = useCallback(async () => {
    const version = ++fetchVersionRef.current
    setLoading(true)
    setError(null)

    try {
      const [latestData, historyData, statusData] = await Promise.all([
        fetchLatest(),
        fetchHistory(),
        fetchStatus()
      ])

      // Discard results if a newer fetch was triggered while this one was in flight
      if (!mountedRef.current || version !== fetchVersionRef.current) return

      setLatest(latestData)
      setHistoryMap(historyData)
      setStatus(statusData)

      const timestamps = Object.values(latestData).map(r => new Date(r.recorded_at))
      if (timestamps.length > 0) {
        setLastUpdate(new Date(Math.max(...timestamps)))
      }
    } catch (err) {
      if (!mountedRef.current || version !== fetchVersionRef.current) return
      console.error('[useSensorData] Error:', err)
      setError(err.message)
    } finally {
      if (mountedRef.current && version === fetchVersionRef.current) setLoading(false)
    }
  }, [fetchLatest, fetchHistory, fetchStatus])

  // Initial fetch
  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  // Realtime subscriptions
  useEffect(() => {
    const channelName = `sensor-${deviceType}-${deviceId}`

    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'sensor_readings',
        filter: `device_type=eq.${deviceType}`
      }, (payload) => {
        const row = payload.new
        if (row.device_id !== deviceId) return

        setLatest(prev => ({
          ...prev,
          [row.metric]: { value: row.value, recorded_at: row.recorded_at }
        }))

        setLastUpdate(new Date(row.recorded_at))

        // Append to historyMap if metric is tracked
        if (historyMetricsRef.current.includes(row.metric)) {
          setHistoryMap(prev => ({
            ...prev,
            [row.metric]: [...(prev[row.metric] || []), { recorded_at: row.recorded_at, value: row.value }]
          }))
        }
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'device_status',
        filter: `device_type=eq.${deviceType}`
      }, (payload) => {
        const row = payload.new
        if (row.device_id !== deviceId) return

        setStatus(prev => ({
          ...prev,
          [row.status_key]: { value: row.value, updated_at: row.updated_at }
        }))
      })
      .subscribe()

    return () => supabase.removeChannel(channel)
  }, [deviceType, deviceId])

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  // Backward compatible: history = historyMap.temperature
  const history = historyMap.temperature || []

  return { latest, history, historyMap, status, loading, error, lastUpdate, refetch: fetchAll }
}

export default useSensorData
